import React from "react";
import { ButtonGroup } from "react-bootstrap";
import { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import "./sitenav.style.scss";
import ModalTrigger from "../Modals/ModalTrigger";
import MenuMobile from "./MenuMobile";
import MenuDesktop from "./MenuDesktop";
import macuLogo from "../../images/macu-logo.svg";
import { FiSearch } from 'react-icons/fi'
import classNames from "classnames";
import { normalizePath, getRegexFromString } from '../../utilities/path';
import { AutomationPlacement, AutomationTestIdElementType, AutomationTestIdFeature, createDataTestId } from '../../utilities/automation';
import Picture from "../Picture";
import useLanguage from "../../utilities/useLanguage";
import { FeatureFlags, getFeatureFlag } from '../../utilities/featureFlags';
import ModalEnum from "../Modals/BaseModal/ModalEnum";
import NavButton, {NavButtonVariants} from "./NavButton";

export const HEADER_HEIGHT = 88;

export default styled(({ className, menu = [], mobileLoginPagePaths = [], utilityNav = [] }) => {
    const [selected, setSelected] = useState(null);
    const [showMobileLogin, setShowMobileLogin] = useState(false);
    const [mobileUtilityNav, setMobileUtilityNav] = useState([]);
    const selectedRef = useRef(selected);
    const enableSpanishHomepage = getFeatureFlag(FeatureFlags.ENABLE_SPANISH_HOMEPAGE);
    const { lang } = useLanguage();
    const logoUrl = enableSpanishHomepage ? (lang === 'en' ? '/' : `/${lang}`) : '/';

    useEffect(() => {
      // Mobile utility nav should be rendered in reversed order
      setMobileUtilityNav([...utilityNav].reverse());
    }, [utilityNav]);

    function select(item, requesting) {
        // We only want to unselect the menu that's currently selected
        // Otherwise we might accidentally close a menu
        // between it opening and the mouseevents getting reset
        if (item === null && requesting === selectedRef.current) {
            setSelected(null);
            selectedRef.current = null;
        } else if (item !== null) { // If switching between tabs we can just send it through
            setSelected(item);
            selectedRef.current = item;
        }
    }

    function onClose(item) {
        // Only close the menu that made this call if it's selected
        if (selectedRef.current === item) {
            selectedRef.current = null;
            setSelected(null);
        }
    }

    useEffect(() => {
        const currentPath = normalizePath(document.location.pathname);

        const show = mobileLoginPagePaths.some(p => {
            return Boolean(currentPath.match(getRegexFromString(p)));
        });
        setShowMobileLogin(show);
    }, [mobileLoginPagePaths]);

    return (
      <>
        <div id="adobe-target-header" className='position-relative' style={{zIndex: 1051}}/>
        <div className={classNames("w-100 bg-white position-relative", className)}>
          <header
              className={classNames("container d-flex align-items-center px-0")}
          >
            {/*Empty drop shadow div to show it above the absolute positioned menu when opened */}
            <div className="drop-shadow position-absolute w-100">
                <div className="inner"></div>
            </div>

              <div className="header-img-container align-self-center text-center text-lg-left">
                  <div style={{ margin: "0 auto" }}>
                      <a href={logoUrl} className="display-inline-block">
                          <Logo />
                      </a>
                  </div>
              </div>
              <MenuDesktop
                  menu={menu}
                  onClose={onClose}
                  selected={selected}
                  onSelect={select}
                  height={HEADER_HEIGHT}/>
              <ButtonGroup className={classNames("py-2 header-buttons d-flex justify-content-end flex-grow-1")}>
                  <div className="d-none d-lg-flex">
                    {utilityNav.map((utilityItem, index) => {
                      const { href, label, class: utilityClass } = utilityItem;
                      if (href === '#login') {
                        return (
                          <ModalTrigger modal={ModalEnum.Login} key={label}>
                            <NavButton customClass={utilityClass} label={label} variant={NavButtonVariants.RED_ROCK} />
                          </ModalTrigger>
                        );
                      }
                      return (
                        <NavButton key={index} customClass={classNames("mr-3", utilityClass)} label={label} href={href} variant={NavButtonVariants.OUTLINE_BLACK} />
                      );
                    })}
                  </div>
                  {showMobileLogin &&
                    <ModalTrigger modal={ModalEnum.Login}>
                      <NavButton customClass='d-lg-none login-button' label={utilityNav.find(item => item.href === '#login')?.label || 'Log in'} variant={NavButtonVariants.RED_ROCK} />
                    </ModalTrigger>
                  }
                  <ModalTrigger modal={ModalEnum.Search} >
                      <a data-test-id={createDataTestId({placement: AutomationPlacement.SITE_HEADER, feature: AutomationTestIdFeature.SEARCH, element: AutomationTestIdElementType.BUTTON, specifier: 'header'})} className="ml-3 modal-search-button d-inline-flex align-items-center"><FiSearch/></a>
                  </ModalTrigger>
              </ButtonGroup>
              <MenuMobile menu={menu} utilityNav={mobileUtilityNav} height={HEADER_HEIGHT} />
          </header>
        </div>
        <Backdrop show={Boolean(selected)} />
      </>

    );
})`
  z-index: 1051 !important;
  header {
    height: ${HEADER_HEIGHT + "px"};

    .drop-shadow {
        top: ${HEADER_HEIGHT + "px"};
        left: 0;
        right: 0;
        z-index: 10000;
        .inner {
            height: 50px;
            margin-top: -20px;
            box-shadow: 0 15px 20px -20px rgba(0, 0, 0, 0.6);
            margin: -50px auto 10px;
            pointer-events: none;
        }
    }

    .header-buttons{
      padding-right: 0px;

      .modal-search-button {
        padding: 0;
        border: none;
        background: transparent;
        color: #000;
        cursor: pointer;
        &:hover {
          color: #000
        }
        svg {
          width: 24px;
          height: 24px;
        }
      }
    }

    @media(max-width: 992px){
      width: 100%;
      max-width: 100% !important;
      justify-content: space-between;
    }
  }
`;

const Logo = styled((props) => {
  return (
      <Picture
        className={"new-logo " + props.className}
        src={macuLogo}
        alt="Mountain America Credit Union"
        dataTestId={createDataTestId({placement: AutomationPlacement.SITE_HEADER, element: AutomationTestIdElementType.IMAGE, specifier: 'macu-logo'})}
      />
  );
})`
width: 213px;
padding-left: 0;
margin-left: 3rem;

@media(max-width: 1440px){
  display: block;
  width: 213px;
  padding-left: 0;
}

@media(max-width: 1290px){
  margin-left: 1.5rem;
}

@media(max-width: 1200px){
  width: 160px;
}

@media(max-width: 1090px){
  margin-left: 0;
}
`;

const Backdrop = styled(({ className = '', show = false}) => {
  return (
      <div className={classNames(className, {'show' : show})}></div>
  )
})`
background-color: rgba(248, 248, 248, .1);
height: 100vh;
left: 0;
position: fixed;
top: 0;
width: 100vw;
opacity: 0;
visibility: hidden;
transition: all .25s linear;
z-index: 1050 !important;

&.show {
  opacity: .95;
  visibility: visible;
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
}
`